import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { stopoverCount } from 'utils/Reserbus';
import { roundTripFareApplies } from 'utils/roundTripFare';
import useGrowthBookFeatureValue from 'components/GrowthBookProvider/useGrowthBookFeatureValue';
import useHybridSearchEnabled from 'hooks/useHybridSearchEnabled';
import DotersResultInvitation from 'components/Doters/DotersResultsInvitation';
import InfiniteScroll from 'components/InfiniteScroll';
import TripItem from 'components/search/TripItem';
import ClosedTripItem from 'components/search/ClosedTripItem';
import OpenTripItem from 'components/search/OpenTripItem';
import PromoUrgencyBannerContainer from 'components/search/PromoUrgencyBannerContainer';
import OpenTicketCTA from 'components/search/OpenTicketCTA';
import NoFilteredResults from 'components/search/NoFilteredResults';
import { Icon, Spacing, Text } from '@reservamos/elements';
import ResultsTitle from '../../../ui/atoms/ResultsTitle';
import useFetchAd from '../../../hooks/useFetchAd';
import ResultsHorizontalLegend from '../ResultsHorizontalLegend';
import 'styles/components/search/SchedulesResults';
import PromotionalCard from '../../../ui/atoms/PromotionalCard';
import CostapassMessage from '../../../ui/atoms/CostaPassMessage';
import useLoyaltyPrograms from '../../../loyalty/context/useLoyaltyPrograms';
import RewardTicketInvalidDialog from '../RewardTicketInvalidDialog';
import NoCategoriesFound from '../NoCategoriesFound';
import {
  groupTripsByTerminalResult,
  shouldGroupTerminalResults,
} from '../../../utils/search/terminalResultGroups';
import { someTripAcceptsEwallet } from '../../../utils/trips/ewalletAvailability';
import { filterRecommendedTripsByAvailability } from '../../../utils/search/recommendedTrips';
import { shouldShowProviderDiscountForSearch } from '../../../utils/passengerCategories';

const TERMINAL_GROUP_INITIAL_TRIPS = 5;
const TERMINAL_GROUP_TRIPS_INCREMENT = 15;
// Prefixes group ids in the filter-excluded list so they never collide with the primary list's.
const FILTER_EXCLUDED_GROUP_ID_PREFIX = 'filter-excluded-';

/**
 * Turn a terminal group key into a safe fragment for DOM ids.
 * @param {string} groupKey - Terminal group key.
 * @returns {string} Key with every character outside [a-zA-Z0-9_-] replaced by '-'.
 */
const sanitizeGroupKeyForId = (groupKey) => groupKey.replace(/[^a-zA-Z0-9_-]/g, '-');

/**
 * TerminalGroupLocationIcon component.
 * Render the outlined location pin used by terminal group headers.
 * @returns {JSX.Element} Location pin icon.
 */
const TerminalGroupLocationIcon = () => (
  <span aria-hidden="true" className="shrink-0">
    <Icon type="PinOutlined" size="S" color="grayMedium" />
  </span>
);

/**
 * TerminalGroupVisibilityIcon component.
 * Render the chevron used to expand or collapse a terminal group.
 * @param {object} props - Component props.
 * @param {boolean} props.isExpanded - Whether the chevron points up for the collapse state.
 * @returns {JSX.Element} Chevron icon.
 */
const TerminalGroupVisibilityIcon = ({ isExpanded }) => (
  <svg
    aria-hidden="true"
    className={`h-4 w-4 ${isExpanded ? 'rotate-180' : ''}`}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

TerminalGroupVisibilityIcon.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
};

/**
 * TripList component.
 * Render a list of trips with filtering, infinite scroll, and various trip types.
 *
 * @param {object} props - way, departureTrip, transportType, trips, paymentPlans, installmentsMinAmount, activeDepartureFilter, activeLocationFilter, tooManyFilters, isNotFiltered, resetTripFilters, selectTrip, isAdLoading, roundTrip, couponCode, discountedTripsByCoupon, isOpenTicketList, tripOpenTicket, isLogged, filtersApplied, showDotersBanner, highLightAsMobile, isHorizontal, passengersNotFound.
 * @param {string} props.way - Specify the direction of the trip (outgoing/return).
 * @param {object} props.departureTrip - Provide the departure trip details.
 * @param {string} props.transportType - Define the type of transport (bus/flight).
 * @param {Array} props.trips - Supply array of available trips.
 * @param {Array} props.recommendedTrips - Recommended trips embedded in terminal groups.
 * @param {Array} props.closedTrips - Closed trips embedded after available trips in terminal groups.
 * @param {object} props.paymentPlans - Provide available payment plans as object with number values.
 * @param {number} props.installmentsMinAmount - Set minimum amount for installments.
 * @param {boolean} props.activeDepartureFilter - Indicate if departure time filter is active.
 * @param {boolean} props.activeLocationFilter - Indicate if location filter is active.
 * @param {boolean} props.tooManyFilters - Indicate whether too many filters are applied.
 * @param {boolean} props.isNotFiltered - Indicate whether no filters are applied.
 * @param {Function} props.onQuickPurchaseRequest - Callback fired for quick purchase requests.
 * @param {Function} props.resetTripFilters - Handle resetting trip filters.
 * @param {Function} props.selectTrip - Handle trip selection.
 * @param {Function} props.onOpenTicketSelect - Handle open-ticket selection in terminal groups; falls back to `selectTrip`.
 * @param {boolean} props.isAdLoading - Indicate whether ads are loading.
 * @param {boolean} props.roundTrip - Indicate round trip configuration.
 * @param {string} props.couponCode - Provide applied coupon code.
 * @param {Array} props.discountedTripsByCoupon - Supply trips with coupon discounts.
 * @param {boolean} props.isOpenTicketList - Indicate whether showing open tickets.
 * @param {*} props.tripOpenTicket - Provide open ticket trip details.
 * @param {boolean} props.isLogged - Indicate whether user is logged in.
 * @param {boolean} props.filtersApplied - Indicate whether filters are applied.
 * @param {boolean} props.showDotersBanner - Control whether to show Doters banner.
 * @param {boolean} props.highLightAsMobile - Control whether to highlight as mobile.
 * @param {boolean} props.isHorizontal - Control whether to use horizontal layout.
 * @param {Array} props.passengersNotFound - Supply array of passengers not found.
 * @param {boolean} props.isRecommendedList - Indicate whether rendering inside the recommended trips list.
 * @param {Function} props.onQuickPurchaseRequest - Handle quick purchase requests.
 * @param {object} props.analyticsSearchData - Normalized search data for analytics.
 * @param {boolean} props.searchIsPolling - Whether search results are still polling.
 * @param {string} props.departureDate - Date currently selected in departure results.
 * @param {object} props.origin - Search origin place.
 * @param {object} props.destination - Search destination place.
 * @param {boolean} props.hidePromoUrgencyBanner - Whether the parent renders the urgency banner.
 * @param {object} props.resultPriceToShow - Wallet price metadata for the current search.
 * @returns {JSX.Element} The rendered trip list component.
 */
const TripList = ({
  way,
  departureTrip,
  transportType,
  trips,
  recommendedTrips,
  closedTrips,
  paymentPlans,
  installmentsMinAmount,
  activeDepartureFilter,
  activeLocationFilter,
  tooManyFilters,
  resetTripFilters,
  selectTrip,
  onOpenTicketSelect,
  isAdLoading,
  roundTrip,
  couponCode,
  discountedTripsByCoupon,
  isOpenTicketList,
  tripOpenTicket,
  isLogged,
  filtersApplied,
  isNotFiltered,
  showDotersBanner,
  highLightAsMobile,
  isHorizontal,
  passengersNotFound,
  isRecommendedList,
  onQuickPurchaseRequest,
  analyticsSearchData,
  searchIsPolling,
  departureDate,
  origin,
  destination,
  hidePromoUrgencyBanner,
  resultPriceToShow,
  isSecondaryList,
  testId,
}) => {
  const [rewardTicketStatus, setRewardTicketStatus] = useState({
    status: '',
    onAccept: () => {},
  });
  const { features } = useSelector((state) => state.whitelabelConfig);
  const passengers = useSelector((state) => state.search?.get('passengers'));
  const searchPassengers = useMemo(() => passengers?.toJS(), [passengers]);
  const {
    ticket: { nit },
    operationNumbers,
  } = useSelector((state) => state.exchange);

  const [showNotCategoriesFound, setShowNotCategoriesFound] = useState(false);
  const passengersNotFoundAlert = useRef();

  const history = useHistory();

  const queryParams = new URLSearchParams(window.location.search);
  const isExchange = queryParams.get('isExchange');

  const {
    costaPass: { userIsLoggedIn: userIsLoggedInWithCostaPass },
    tripsWithWalletDiscount,
    thereAreTripsWithWalletDiscount,
  } = useLoyaltyPrograms();
  const { t } = useTranslation();
  const hybridSearchEnabled = useHybridSearchEnabled();
  const categoryBasedPricing = useGrowthBookFeatureValue('category_based_pricing');
  const halfPriceCategoryFallback = useGrowthBookFeatureValue('half_price_category_fallback');
  // Expanded group counts belong to one search: a new route or date discards them, so every group
  // restarts at the initial count (also when returning to an earlier search). Filters and polled
  // results keep the same key, so expanded groups stay open.
  const searchOriginId = useSelector((state) => state.search?.get('originId'));
  const searchDestinationId = useSelector((state) => state.search?.get('destinationId'));
  const searchDepartureDate = useSelector((state) => state.search?.get('departureDate'));
  const searchReturnDate = useSelector((state) => state.search?.get('returnDate'));
  const terminalGroupSearchKey = [
    searchOriginId,
    searchDestinationId,
    searchDepartureDate,
    searchReturnDate,
  ].join('|');
  const [terminalGroupVisibility, setTerminalGroupVisibility] = useState({
    searchKey: terminalGroupSearchKey,
    counts: {},
  });
  const isCurrentTerminalGroupSearch = terminalGroupVisibility.searchKey === terminalGroupSearchKey;
  // Persist the reset during render (React's "adjust state on prop change" pattern); the `{}`
  // fallback keeps this pass from painting the previous search's counts.
  if (!isCurrentTerminalGroupSearch) {
    setTerminalGroupVisibility({ searchKey: terminalGroupSearchKey, counts: {} });
  }
  const terminalGroupVisibleCounts = isCurrentTerminalGroupSearch
    ? terminalGroupVisibility.counts
    : {};

  const isNewFilters = features.NEW_FILTERS_VERSION;
  const isNewResultsDesign = features.NEW_RESULTS_DESIGN;
  // Same tenant gate as the global open-ticket CTA below, applied to per-group open tickets.
  const showGroupedOpenTickets = Boolean(
    (!isNewResultsDesign && features.SHOW_OPEN_TICKET_ON_RESULTS) ||
      (isNewResultsDesign && features.SHOW_OPEN_TICKET_BUTTON),
  );

  const tripData = trips[0] || {
    origin: {
      cityId: '',
    },
    destination: {
      cityId: '',
    },
    providerId: '',
  };

  useFetchAd(
    {
      origin: tripData.origin ? tripData.origin.cityId : '',
      destination: tripData.destination ? tripData.destination.cityId : '',
      line: tripData.lineId || (tripData.carrier ? tripData.carrier.carrierId : ''),
    },
    'results',
    // The guard inside `useFetchAd` is a useState local to the hook instance, so every mount asks
    // for its own ad in the same space. A secondary list opts out instead of adding one more
    // request for a space the page has already filled.
    !isSecondaryList && trips.length > 1,
  );

  const exchangeOpenTicketSelected = useRef();

  useEffect(() => {
    // Checks if user comes from exchange ticket
    // If operationNumbers & nit are undefined redirects the user to exchange
    if (isExchange && !operationNumbers && !nit) {
      history.push(`/exchange`);
    }
  }, [isExchange, operationNumbers, nit, history]);

  useEffect(() => {
    if (
      !exchangeOpenTicketSelected.current &&
      operationNumbers?.length &&
      nit &&
      trips.length === 1
    ) {
      exchangeOpenTicketSelected.current = true;
      selectTrip(trips[0], 0);
    }
  }, [operationNumbers?.length, nit, trips, selectTrip]);

  useEffect(() => {
    if (!isNotFiltered && passengersNotFound.length && !passengersNotFoundAlert.current) {
      passengersNotFoundAlert.current = true;
      setShowNotCategoriesFound(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengersNotFound]);

  let filterMessage = `${t('search:label.trips_filtered_by')} `;
  let filterContainer;
  let noResultsContainer;

  if (activeDepartureFilter && activeLocationFilter) {
    filterMessage += t('search:label.schedule_and_location');
  } else if (activeDepartureFilter) {
    filterMessage += t('search:label.schedule');
  } else if (activeLocationFilter) {
    filterMessage += t('search:label.location');
  }

  if (!isNewFilters && (activeDepartureFilter || activeLocationFilter)) {
    filterContainer = <p className="trip-message">{filterMessage}</p>;
  }

  if (tooManyFilters && !isNotFiltered) {
    noResultsContainer = <NoFilteredResults resetTripFilters={resetTripFilters} />;
  }

  /**
   * Render open trips with service tags.
   * @returns {JSX.Element|null} The rendered open trips or null if not filtered.
   */
  const renderOpenTrips = () => {
    if (isNotFiltered) return null;

    const sortedTrips = trips.sort((a, b) => (a.service > b.service ? 1 : -1));
    let currentService = '';

    return sortedTrips.map((trip) => {
      let showServiceTag = false;
      const tripService = trip.service;

      if (currentService === tripService) {
        if (showServiceTag === true) showServiceTag = false;
      } else {
        currentService = tripService;
        showServiceTag = true;
      }

      return (
        <>
          {showServiceTag && (
            <Text color="grayMedium" size="M" fontFamily="secondary" weight="bold">
              {t('search:service', {
                service: t('purchase:services', { context: currentService }),
              })}
            </Text>
          )}

          <OpenTripItem
            key={trip.id}
            trip={trip}
            roundTrip={roundTrip}
            way={way}
            onSelectClick={selectTrip}
          />
        </>
      );
    });
  };

  /**
   * Closes the not categories found alert.
   */
  const handleOnCloseNotCategoriesFound = () => {
    setShowNotCategoriesFound(false);
  };

  /** Function to handle the click in the accept button in the not valid flat fare trip modal */
  const handleOnAcceptFlatFare = (onAccept) => () => {
    /**
     * @todo This method is probably not needed anymore, but it's here just in case
     */
    setRewardTicketStatus({
      status: 'continue',
      onAccept: () => {},
    });
    onAccept();
  };

  /** Function to handle the click in the cancel button in the not valid flat fare trip modal */
  const handleOnRejectFlatFare = () => {
    setRewardTicketStatus({ status: '', onAccept: () => {} });
  };

  // The electronic wallet ("Monedero Electrónico" / CostaPass) only applies to the routes the
  // carrier enrolls in the program, and it reports that per schedule in `availableWallets`. The
  // invitation to sign in is shown only when the searched route has at least one such schedule.
  const routeAcceptsEwallet = useMemo(() => someTripAcceptsEwallet(trips), [trips]);

  /**
   * Render normal trips with pricing and trip details.
   * @returns {JSX.Element[]} Array of rendered trip components.
   */
  const renderTrip = (trip, index, isTerminalGrouped = false) => {
    let pricingObject = trip;
    let pricingKey = 'pricing';

    if (trip.isAd) {
      if (trips.length === 1 || (!isAdLoading && !trip.adData)) return null;

      const { body, imageUrl, link } = trip.adData || {};
      const adKey = `ad-${index}`;

      return (
        <PromotionalCard
          key={adKey}
          message={body}
          iconUrl={imageUrl}
          linkUrl={link}
          linkText="Ver mas"
          isLoading={isAdLoading}
        />
      );
    }

    if (roundTrip && way === 'departure') {
      if (trip.departureRoundTripPricing !== undefined) {
        pricingKey = 'departureRoundTripPricing';
      } else {
        pricingKey = 'pricing';
      }
    } else if (way === 'return') {
      if (features.VALIDATE_ROUND_TRIP_DISCOUNT) {
        if (roundTripFareApplies({ departureLeg: departureTrip, returnLeg: trip })) {
          pricingKey = 'roundTripPricing';
        } else {
          pricingKey = 'pricing';
        }
      } else {
        pricingKey = 'roundTripPricing';
      }
    }

    const hasDiscountType = Boolean(trip[pricingKey].discount_type);

    const discountedTrips = thereAreTripsWithWalletDiscount
      ? tripsWithWalletDiscount
      : discountedTripsByCoupon;
    if (couponCode || thereAreTripsWithWalletDiscount) {
      const tripDiscounted = discountedTrips.find(({ id }) => id === trip.id) || trip;
      if (tripDiscounted && tripDiscounted[pricingKey]) pricingObject = tripDiscounted;
    }

    const providerDiscount = trip[pricingKey].providerDiscount || { amount: 0 };
    const { total } = pricingObject[pricingKey];
    const totalBeforeDiscount =
      trip[pricingKey].totalBeforeDiscount || providerDiscount.amount + total;

    const { officePrice } = trip[pricingKey];

    const tripTransportType = features.PROVIDERS_SELECTION_ENABLED
      ? transportType
      : trip.transportType;

    const signMessagesInvitationCostapass = features.COSTAPASS_ENABLED &&
      routeAcceptsEwallet &&
      !userIsLoggedInWithCostaPass && <CostapassMessage show />;

    return (
      <React.Fragment key={trip.id}>
        <TripItem
          departureTrip={departureTrip}
          trip={trip}
          paymentPlans={paymentPlans}
          hasDiscountType={hasDiscountType}
          hasRouteDetails={Boolean(trip.hasRouteDetails)}
          installmentsMinAmount={installmentsMinAmount}
          position={index + 1}
          type={trip.type || transportType}
          id={trip.id}
          origin={trip.origin}
          destination={trip.destination}
          departure={trip.departure}
          arrival={trip.arrival}
          duration={trip.duration}
          variableDepartureTime={trip.variableDepartureTime || false}
          providerDiscount={trip[pricingKey].providerDiscount}
          providerDiscounts={trip[pricingKey].providerDiscounts}
          totalBeforeDiscount={totalBeforeDiscount}
          officePrice={officePrice}
          total={pricingObject[pricingKey].total}
          serviceType={tripTransportType === 'bus' ? trip.line.serviceType : null}
          stops={stopoverCount(trip)}
          capacity={trip.capacity}
          availability={trip.availability}
          availableCategories={trip.passengerTypes}
          hasTransit={trip.hasTransit}
          stopoverPlace={trip.stopoverPlace}
          selectTrip={selectTrip}
          way={way}
          isRoundTrip={roundTrip}
          isOpenTicket={trip.isOpenTicket}
          isFromOpenTicketList={isOpenTicketList}
          supportWoman={trip.supportWoman}
          filtersApplied={filtersApplied}
          isLogged={isLogged}
          rewardTicketStatus={rewardTicketStatus}
          setRewardTicketStatus={setRewardTicketStatus}
          pricing={pricingObject[pricingKey]}
          highLightAsMobile={highLightAsMobile}
          allowsSeatSelection={trip.allowsSeatSelection}
          isRecommendedList={isRecommendedList}
          isTerminalGrouped={isTerminalGrouped}
          onQuickPurchaseRequest={onQuickPurchaseRequest}
        />
        {!isNotFiltered && index === 6 && signMessagesInvitationCostapass}
      </React.Fragment>
    );
  };

  /**
   * Render an open-ticket CTA within its concrete terminal result group.
   * @param {object} trip - Open ticket trip returned for the terminal route.
   * @returns {JSX.Element} Open ticket CTA.
   */
  const renderOpenTicket = (trip) => {
    const shouldShowProviderDiscount = shouldShowProviderDiscountForSearch(
      searchPassengers,
      trip.passengerTypes,
      Boolean(categoryBasedPricing),
      Boolean(halfPriceCategoryFallback),
    );

    return (
      <OpenTicketCTA
        key={`open-ticket-${trip.id}`}
        openTrip={trip}
        way={way}
        isRoundTrip={roundTrip}
        onSelect={onOpenTicketSelect || selectTrip}
        isTerminalGrouped
        showWalletPoints={resultPriceToShow?.priceType === 'wallet'}
        walletType={resultPriceToShow?.walletType}
        shouldShowProviderDiscount={shouldShowProviderDiscount}
      />
    );
  };

  const groupTerminalResults = shouldGroupTerminalResults({
    featureEnabled: hybridSearchEnabled,
    origin,
    destination,
    isHorizontal,
    isRecommendedList,
  });

  /**
   * Render the title for a terminal result group.
   * @param {Object} group - Terminal result group with label, originLabel, destinationLabel and highlight.
   * @param {string} key - Unique group key.
   * @param {string} headingId - Accessible heading identifier.
   * @param {boolean} isUnavailable - Whether the group belongs to unavailable schedules.
   * @returns {JSX.Element|null} Group header element.
   */
  const renderTerminalGroupHeader = (group, key, headingId, isUnavailable = false) => {
    const { label, originLabel, destinationLabel, highlight } = group;
    if (!label) return null;

    const originClassName = highlight === 'origin' ? 'font-semibold' : 'font-normal text-gray-500';
    const destinationClassName =
      highlight === 'destination' ? 'font-semibold' : 'font-normal text-gray-500';

    return (
      <div
        id={headingId}
        key={`terminal-group-${key}`}
        className={`flex gap-2 text-gray-800 ${
          isUnavailable
            ? 'items-start px-1 pb-2 pt-4'
            : 'mb-3 items-center rounded-t-3xl bg-gray-200 bg-opacity-50 px-4 py-3'
        }`}
      >
        <TerminalGroupLocationIcon />
        <span className="flex min-w-0 flex-col md:flex-row md:items-baseline md:gap-2">
          <span className={`${originClassName} text-sm leading-tight md:text-base`}>
            {originLabel}
          </span>
          {destinationLabel && (
            <>
              <span aria-hidden="true" className="hidden text-gray-500 md:inline">
                -
              </span>
              <span
                className={`${destinationClassName} mt-1 text-xs leading-tight md:mt-0 md:text-base`}
              >
                {destinationLabel}
              </span>
            </>
          )}
        </span>
      </div>
    );
  };

  /**
   * Render the per-group control that exposes or collapses additional schedules.
   * @param {string} groupKey - Unique group key.
   * @param {number} visibleCount - Current number of visible trips in the group.
   * @param {number} remainingTrips - Number of hidden trips in the group.
   * @param {number} groupSize - Total schedules currently in the group after filters.
   * @returns {JSX.Element|null} Group visibility button element.
   */
  const renderTerminalGroupVisibilityButton = (
    groupKey,
    visibleCount,
    remainingTrips,
    groupSize,
  ) => {
    const isExpanded =
      visibleCount > TERMINAL_GROUP_INITIAL_TRIPS && groupSize > TERMINAL_GROUP_INITIAL_TRIPS;
    const hasMoreTrips = remainingTrips > 0;
    if (!isExpanded && !hasMoreTrips) return null;

    const shouldCollapse = isExpanded && !hasMoreTrips;

    const nextCount = Math.min(TERMINAL_GROUP_TRIPS_INCREMENT, remainingTrips);
    /** Toggle between the initial schedules and the next batch for this terminal. */
    const handleClick = () =>
      setTerminalGroupVisibility((current) => {
        const currentCounts = current.searchKey === terminalGroupSearchKey ? current.counts : {};
        return {
          searchKey: terminalGroupSearchKey,
          counts: {
            ...currentCounts,
            [groupKey]: shouldCollapse
              ? TERMINAL_GROUP_INITIAL_TRIPS
              : (currentCounts[groupKey] || TERMINAL_GROUP_INITIAL_TRIPS) +
                TERMINAL_GROUP_TRIPS_INCREMENT,
          },
        };
      });

    return (
      <button
        key={`terminal-group-visibility-${groupKey}`}
        type="button"
        className="mx-auto flex items-center gap-2 py-4 text-sm font-normal text-gray-400"
        onClick={handleClick}
      >
        <span>
          {shouldCollapse
            ? t('trips:show_only_initial_schedules', {
                count: TERMINAL_GROUP_INITIAL_TRIPS,
              })
            : t('trips:load_more_schedules', { count: nextCount })}
        </span>
        <TerminalGroupVisibilityIcon isExpanded={shouldCollapse} />
      </button>
    );
  };

  /**
   * Render normal trips with pricing and trip details.
   * @returns {JSX.Element[]} Array of rendered trip components.
   */
  const renderNormalTrips = () => {
    if (!groupTerminalResults) return trips.map((trip, index) => renderTrip(trip, index));

    const groupIdPrefix = isNotFiltered ? FILTER_EXCLUDED_GROUP_ID_PREFIX : '';
    const visibleRecommendedTrips = filterRecommendedTripsByAvailability(
      recommendedTrips,
      searchPassengers,
    ).filter((trip) => !trip?.openTicket);
    // The filter-excluded list skips the trips the primary list already shows as recommended.
    const displayedRecommendedIds = new Set(
      visibleRecommendedTrips.map((trip) => trip?.id).filter(Boolean),
    );
    const listTrips = isNotFiltered
      ? trips.filter((trip) => !trip?.id || !displayedRecommendedIds.has(trip.id))
      : trips;
    const listRecommendedTrips = isNotFiltered ? [] : recommendedTrips;
    const groupRecommendedTrips = isNotFiltered ? [] : visibleRecommendedTrips;

    const tripOriginalIndexes = new Map();
    [...listRecommendedTrips, ...listTrips].forEach((trip, index) => {
      if (trip?.id && !tripOriginalIndexes.has(trip.id)) {
        tripOriginalIndexes.set(trip.id, index);
      }
    });

    const visibleTrips = listTrips.filter((trip) => !trip?.openTicket);
    const openTicketTrips = showGroupedOpenTickets
      ? listTrips.filter((trip) => trip?.openTicket)
      : [];
    const availableGroups = groupTripsByTerminalResult({
      trips: [...visibleTrips, ...openTicketTrips],
      recommendedTrips: groupRecommendedTrips,
      origin,
      destination,
    });
    const availableTripIds = new Set(trips.map((trip) => trip?.id).filter(Boolean));
    const closedTripsToGroup = closedTrips.filter(
      (trip) => !trip?.id || !availableTripIds.has(trip.id),
    );
    const closedGroups = groupTripsByTerminalResult({
      trips: closedTripsToGroup,
      origin,
      destination,
    });
    const availableResults = availableGroups.map((group) => {
      const scheduleTrips = group.trips.filter((trip) => !trip?.openTicket);
      const groupOpenTickets = group.trips.filter((trip) => trip?.openTicket);
      const visibleCount = terminalGroupVisibleCounts[group.key] || TERMINAL_GROUP_INITIAL_TRIPS;
      const visibleTrips = scheduleTrips.slice(0, visibleCount);
      const remainingTrips = scheduleTrips.length - visibleTrips.length;
      const headingId = `${groupIdPrefix}terminal-group-${sanitizeGroupKeyForId(group.key)}`;

      const scheduleResults = visibleTrips.map((trip) => {
        const originalIndex = tripOriginalIndexes.get(trip?.id);
        return renderTrip(trip, originalIndex ?? 0, true);
      });

      if (!group.label) {
        return (
          <React.Fragment key={group.key}>
            {scheduleResults}
            {groupOpenTickets.map(renderOpenTicket)}
          </React.Fragment>
        );
      }

      return (
        <section
          key={group.key}
          aria-labelledby={headingId}
          className="mb-6"
          data-testid={`${groupIdPrefix}terminal-result-group-${group.key}`}
        >
          {renderTerminalGroupHeader(group, group.key, headingId)}
          <div className="flex flex-col gap-2.5">
            {scheduleResults}
            {groupOpenTickets.length > 0 && (
              <div data-testid="terminal-group-open-ticket">
                {groupOpenTickets.map(renderOpenTicket)}
              </div>
            )}
          </div>
          {renderTerminalGroupVisibilityButton(
            group.key,
            visibleCount,
            remainingTrips,
            scheduleTrips.length,
          )}
        </section>
      );
    });

    if (!closedGroups.length) return availableResults;

    const closedResults = closedGroups.flatMap((group) => [
      renderTerminalGroupHeader(
        group,
        `closed-${group.key}`,
        `${groupIdPrefix}closed-terminal-group-${sanitizeGroupKeyForId(group.key)}`,
        true,
      ),
      ...group.trips.map((trip, index) => (
        <ClosedTripItem
          key={trip.id}
          trip={trip}
          position={index}
          selectTrip={selectTrip}
          isRoundTrip={roundTrip}
          way={way}
          isLogged={isLogged}
          departureTrip={departureTrip}
          rewardTicketStatus={rewardTicketStatus}
          setRewardTicketStatus={setRewardTicketStatus}
          filtersApplied={filtersApplied}
          features={features}
          origin={origin}
          destination={destination}
        />
      )),
    ]);

    return [
      ...availableResults,
      <ResultsTitle key="closed-terminal-schedules" sectionTitle="closedSchedules" />,
      ...closedResults,
    ];
  };

  const usesTerminalGroups = !isOpenTicketList && groupTerminalResults;
  const showGlobalOpenTicket = !usesTerminalGroups;
  const openTicketResult =
    (showGlobalOpenTicket &&
      !isNewResultsDesign &&
      features.SHOW_OPEN_TICKET_ON_RESULTS &&
      tripOpenTicket) ||
    null;
  const openTicketButton =
    (showGlobalOpenTicket &&
      isNewResultsDesign &&
      features.SHOW_OPEN_TICKET_BUTTON &&
      tripOpenTicket) ||
    null;
  const tripItems = isOpenTicketList ? renderOpenTrips() : renderNormalTrips();

  return (
    <>
      <div className="trip-container fade-in" data-testid={testId}>
        {filterContainer}
        <Spacing size="M" vertical>
          {showNotCategoriesFound && (
            <NoCategoriesFound
              passengersNotFound={passengersNotFound}
              onClose={handleOnCloseNotCategoriesFound}
            />
          )}
          {noResultsContainer}

          {!hidePromoUrgencyBanner && (
            <PromoUrgencyBannerContainer
              trips={trips}
              roundTrip={roundTrip}
              way={way}
              isOpenTicketList={isOpenTicketList}
              isRecommendedList={isRecommendedList}
              analyticsSearchData={analyticsSearchData}
              searchIsPolling={searchIsPolling}
              departureDate={departureDate}
            />
          )}

          {features.USE_HORIZONTAL_RESULTS && <ResultsHorizontalLegend />}

          {openTicketResult}

          {usesTerminalGroups ? (
            tripItems
          ) : (
            <InfiniteScroll
              isHorizontal={isHorizontal}
              items={tripItems}
              usesSideBar={features.SEARCH_SIDEBAR_ENABLED}
            />
          )}
          {(showDotersBanner || openTicketButton) && (
            <Spacing
              isResponsive
              responsiveSize="medium"
              justifyContent={showDotersBanner ? 'space-between' : 'flex-end'}
              alignItems="flex-end"
              responsiveColumnReverse
            >
              {showDotersBanner && <DotersResultInvitation />}
              {openTicketButton}
            </Spacing>
          )}
        </Spacing>
        {!isNotFiltered && rewardTicketStatus.status === 'invalid' && (
          <RewardTicketInvalidDialog
            onAccept={handleOnAcceptFlatFare(rewardTicketStatus.onAccept)}
            onReject={handleOnRejectFlatFare}
          />
        )}
      </div>
    </>
  );
};

TripList.propTypes = {
  way: PropTypes.string.isRequired,
  departureTrip: PropTypes.object,
  transportType: PropTypes.string,
  trips: PropTypes.array.isRequired,
  recommendedTrips: PropTypes.array,
  closedTrips: PropTypes.array,
  paymentPlans: PropTypes.objectOf(PropTypes.number).isRequired,
  installmentsMinAmount: PropTypes.number.isRequired,
  activeDepartureFilter: PropTypes.bool.isRequired,
  activeLocationFilter: PropTypes.bool.isRequired,
  tooManyFilters: PropTypes.bool.isRequired,
  resetTripFilters: PropTypes.func.isRequired,
  selectTrip: PropTypes.func.isRequired,
  onOpenTicketSelect: PropTypes.func,
  isAdLoading: PropTypes.bool.isRequired,
  roundTrip: PropTypes.bool.isRequired,
  couponCode: PropTypes.string,
  discountedTripsByCoupon: PropTypes.array,
  isOpenTicketList: PropTypes.bool,
  tripOpenTicket: PropTypes.any,
  isLogged: PropTypes.bool,
  filtersApplied: PropTypes.bool,
  isNotFiltered: PropTypes.bool,
  isSecondaryList: PropTypes.bool,
  testId: PropTypes.string,
  showDotersBanner: PropTypes.bool,
  highLightAsMobile: PropTypes.bool,
  isHorizontal: PropTypes.bool,
  passengersNotFound: PropTypes.array,
  isRecommendedList: PropTypes.bool,
  onQuickPurchaseRequest: PropTypes.func,
  analyticsSearchData: PropTypes.object,
  searchIsPolling: PropTypes.bool,
  departureDate: PropTypes.string,
  origin: PropTypes.object,
  destination: PropTypes.object,
  hidePromoUrgencyBanner: PropTypes.bool,
  resultPriceToShow: PropTypes.shape({
    priceType: PropTypes.string,
    walletType: PropTypes.string,
  }),
};

TripList.defaultProps = {
  passengersNotFound: [],
  recommendedTrips: [],
  closedTrips: [],
  onQuickPurchaseRequest: null,
  analyticsSearchData: null,
  searchIsPolling: false,
  departureDate: null,
  hidePromoUrgencyBanner: false,
  onOpenTicketSelect: null,
  isSecondaryList: false,
  testId: undefined,
};

export default TripList;
